import React, { useContext, useEffect} from "react";
import { DISABLED_PLACEMENTS, DISABLED_PROGRAM_TYPES, mainContext } from "@ts/constants";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { RowNode, ValueFormatterParams } from "ag-grid-community";
import {
    titleCase,
    numericValueFormatter,
    isDisabledUI,
    onPlacementSelectionChanged
} from "@ts/utils";
import { customFilterOptions } from "@ts/constants";
import {
    ChangeInValueCellRenderer,
    StatusCellRenderer,
    TargetCellRenderer,
    FilterTextFormatter,
    StatusBadgeCellRenderer,
    TagsCellRenderer,
    ProgramTypeCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import {
    useUpdateModule,
    useGetModulesData,
    usePermissionCheck,
} from "@hooks/AllCustomHooks";
import moment from "moment";
import { useSelector } from "react-redux";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import {
    ColorHex,
    ICustomCellRendererParams,
    IPlacements,
    IMainContext,
    ParentModule,
    SubModule,
    TCustomFilterOption,
} from "@ts/common-types";
import { Popover } from "antd";
import { ImInfo } from "react-icons/im";
import { PiBroadcastFill } from "react-icons/pi";
import { filter } from "underscore";
import CustomCampaignTagFilter from "@/components/filters/CustomCampaignTagFilter";
import UpdateBidOrBudgetModal from "@/components/modals/UpdateBidOrBudgetModal";

const Placements: React.FC = React.memo(() => {
    const { placementsLoading, placementsGridApi, setPlacementsGridApi } =
        useContext<IMainContext>(mainContext);
    const updateBidOrBudgetModalDisplay = useSelector((state: any) => state.updateBidOrBudgetModalDisplay.value);

    const { canCreateRule } = usePermissionCheck();

    const placementsData = useSelector(
        (state: IPlacements) => state.placementsData.value
    );

    const smartBidInfoHeaderComponent = (params: any) => {
        const content = (
            <div
                className="text-center m-0"
                style={{ width: "12.8rem", height: "5rem", fontSize: "15px" }}
            >
                <PiBroadcastFill
                    style={{
                        color: ColorHex.GREY,
                        fontSize: "15px",
                        marginLeft: "7px",
                    }}
                />{" "}
                icon suggests that smart bid has been enabled for the keyword
                corresponding to it
            </div>
        );

        return (
            <>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-bolder me-3">
                        {params.displayName.toUpperCase()}
                    </div>
                    {!!channelOptions.isSmartInfoRequired && (
                        <Popover
                            styles={{ body: { padding: "10px" } }}
                            content={content}
                        >
                            <ImInfo
                                style={{ cursor: "pointer", fontSize: "12px" }}
                            />
                        </Popover>
                    )}
                </div>
            </>
        );
    };

    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.PLACEMENTS,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });
    const channelOptions = useGetModulesData({
        type: "channel-options",
        activeModuleName: SubModule.PLACEMENTS,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });
    const placementsColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "keyword_name",
                headerName: "Target",
                minWidth: 250,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                headerComponent: smartBidInfoHeaderComponent,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                checkboxSelection: true,
                tooltipField: "keyword_name",
                cellRenderer: TargetCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                },
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
            },
            {
                field: "is_archived_entity",
                headerName: "Is_Archived",
                hide: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value
                        ? "True"
                        : params.node.rowPinned
                        ? ""
                        : "False",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "bid",
                headerName: "Bid",
                pinned: "left",
                minWidth: gridParams["bid"].minWidth,
                maxWidth: gridParams["bid"].maxWidth,
                lockPinned: true,
                cellClass: "lock-pinned",
                cellRenderer: gridParams["bid"].cellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filter: "agNumberColumnFilter",
                filterValueGetter: (params: ICustomCellRendererParams) => {
                    return {
                        bid: params.data.bid,
                        suggestedMin: params.data.exact_suggested_min,
                        suggestedMax: params.data.exact_suggested_max,
                    };
                },
                filterParams: {
                    filterOptions: filter(
                        customFilterOptions["numberWithoutChanges"],
                        (option: TCustomFilterOption) =>
                            gridParams["bid"].filterOptions.includes(
                                option.displayKey
                            )
                    ),
                },
            },
            {
                field: "state",
                headerName: "Bid Status",
                pinned: "left",
                maxWidth: 100,
                lockPinned: true,
                cellRenderer: (params: ICustomCellRendererParams) =>
                    StatusCellRenderer(params, "keyword"),
                cellClass: "lock-pinned",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "ad_group_name",
                headerName: "Ad Group",
                filter: "agTextColumnFilter",
                cellRenderer: StatusBadgeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                },
            },
            {
                field: "serving_status",
                headerName: "Serving Status",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                pinned: "left",
                lockPinned: true,
                hide: true,
                maxWidth: 130,
                minWidth: 120,
                cellClass: ["lock-pinned", "text-center"],
                cellRenderer: StatusBadgeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: false,
                },
            },
            {
                field: "targetingtype",
                headerName: "Campaign Type",
                filter: "agSetColumnFilter",
                hide: true,
                maxWidth: 180,
                minWidth: 170,
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                field: "record_type",
                headerName: "Keyword Type",
                filter: "agSetColumnFilter",
                filterParams: {
                    showTooltips: true,
                    valueFormatter: FilterTextFormatter,
                    values: ["keyword", "target"],
                },
                maxWidth: 110,
                minWidth: 100,
                valueFormatter: (params: ValueFormatterParams) =>
                    "record_type" in channelOptions.formatText
                        ? channelOptions.formatText.record_type[params.value]
                        : titleCase(params.value),
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    suppressNullValueText: 0,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                headerName: "Indirect Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_view_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "latest_top_viewed_pos",
                headerName: "Latest Position",
                minWidth: 90,
                maxWidth: 100,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },

                cellRendererParams: {
                    hasPercent: false,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "avg_top_viewed_pos",
                headerName: "Avg Position",
                minWidth: 90,
                maxWidth: 100,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },

                cellRendererParams: {
                    hasPercent: false,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "search_volume",
                headerName: "Search Vol .",
                minWidth: 120,
                maxWidth: 130,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPercent: false,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "search_volume_monthly",
                headerName: "Search Vol .",
                minWidth: 120,
                maxWidth: 130,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPercent: false,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sponsored_sov",
                headerName: "Sov",
                minWidth: 120,
                maxWidth: 130,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPercent: true,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "AOV",
                cellStyle: { whiteSpace: "normal" },
                field: "aov",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                    hideInfinite: true,
                },
            },
            {
                headerName: "CTR",
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: gridParams["cpc"]?.headerName || "CPC",
                field: "cpc",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPM",
                field: "cpm",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPA",
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Top ASIN",
                cellStyle: { whiteSpace: "normal" },
                field: "top_product",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "% Spend",
                cellStyle: { whiteSpace: "normal" },
                field: "percent_spend",
                hide: true,
            },
            {
                field: "creation_date",
                headerName: "Creation Date",
                filter: "agDateColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["date"],
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    !params.node.rowPinned
                        ? params.value !== ""
                            ? moment(params.value, "YYYY-MM-DD").format(
                                  "YYYY-MM-DD"
                              )
                            : "NA"
                        : "",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "match_type",
                headerName: "Match Type",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value),
                filter: "agTextColumnFilter",
                filterParams: {
                    showTooltips: true,
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "kwd_class",
                headerName: "Keyword Class",
                filter: "agSetColumnFilter",
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : params.value,
                filterParams: {
                    showTooltips: true,
                    valueFormatter: (params: ValueFormatterParams) =>
                        params.value ? titleCase(params.value) : params.value,
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellClass: "text-center",
                cellRenderer: ProgramTypeCellRenderer,
                minWidth: 100,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
            },
            {
                field: "campaign_name",
                headerName: "Campaign",
                filter: "agTextColumnFilter",
                tooltipField: "campaign_name",
                cellRenderer: StatusBadgeCellRenderer,
                minWidth: 160,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                    isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
                },
            },
            {
                field: "portfolio_name",
                headerName: "Portfolio",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "portfolio_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sys_campaign_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sys_adgroup_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "asin_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sys_ams_keyword_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "campaign_tags",
                headerName: "Campaign Tags",
                filter: CustomCampaignTagFilter,
                minWidth: 200,
                cellRenderer: TagsCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "percent_utilisation",
                headerName: "Percent Utilisation",
                filter: "agNumberColumnFilter",
                minWidth: 115,
                maxWidth: 115,
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: false,
                    decimalPlace: 2,
                    hasSingleValue: true,
                },
            },
        ],
        activeModuleName: SubModule.PLACEMENTS,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });

    const updateModule = useUpdateModule(SubModule.PLACEMENTS);

    useEffect(() => {
        sessionStorage.setItem("trendType", "placements");
        // updateModule();
    }, []);

    return (
        <>
            <div className="wrapper" style={{ flex: "1 1 0" }}>
                <div
                    id="placements-grid"
                    className="ag-theme-alpine border-top"
                    style={{ height: "100%", width: "100%" }}
                >
                    <AgGridLayout
                        setGridApi={setPlacementsGridApi}
                        rowData={placementsData}
                        columnDefs={placementsColumnDefs}
                        onSelectionChanged={onPlacementSelectionChanged}
                        updateModule={updateModule}
                        loading={placementsLoading}
                        isTotalRowsReq={true}
                        isRowSelectable={(rowNode: RowNode) => {
                            return !(
                                rowNode.data.is_archived_entity ||
                                isDisabledUI([
                                    rowNode.data.serving_status,
                                    rowNode.data.state,
                                ]) ||
                                DISABLED_PROGRAM_TYPES.includes(
                                    rowNode.data.program_type
                                ) || (rowNode.data.is_keywords_added && DISABLED_PLACEMENTS.includes(rowNode.data.keyword_name))
                            );
                        }}
                        rowHeight={gridParams?.rowHeight}
                    />
                </div>
                 {updateBidOrBudgetModalDisplay && <UpdateBidOrBudgetModal type="bid" />}
            </div>
        </>
    );
});

export default Placements;
